import mapboxgl from "mapbox-gl";
import { Map } from "mapbox-gl";

interface PinLineFeature {
  type: "Feature";
  geometry: {
    type: "LineString";
    coordinates: [number, number][];
  };
  properties: {
    ApplicationStage: string;
  };
}

interface POIData {
  features: Array<{
    geometry: {
      coordinates: [number, number];
    };
    properties: {
      ApplicationStage: string;
    };
  }>;
}

// Use object instead of if-chains (faster lookups and more readable)
const zoomOffsets: [number, number][] = [
  [15, -65],
  [17, -90],
  [20, -110],
  [24, -170],
];

export const calculatePinLineOffset = (zoom: number): number => {
  for (const [z, offset] of zoomOffsets) {
    if (zoom < z) return offset;
  }
  return -170;
};

export const createPinLineFeature = (
  map: Map,
  feature: POIData["features"][0],
  offset: number
): PinLineFeature | null => {
  const [lon, lat] = feature.geometry.coordinates;

  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;

  try {
    const basePoint = map.project([lon, lat]);

    if (!Number.isFinite(basePoint.x) || !Number.isFinite(basePoint.y)) {
      return null;
    }

    const offsetPoint = basePoint.add(new mapboxgl.Point(0, offset));
    const floatingGeoPoint = map.unproject(offsetPoint);

    if (
      !Number.isFinite(floatingGeoPoint.lng) ||
      !Number.isFinite(floatingGeoPoint.lat)
    ) {
      return null;
    }

    // Distance check to avoid glitch
    const dx = floatingGeoPoint.lng - lon;
    const dy = floatingGeoPoint.lat - lat;
    const distanceSquared = dx * dx + dy * dy;
    const maxDistanceSquared = 0.05 * 0.05;

    if (distanceSquared > maxDistanceSquared) {
      return null;
    }

    return {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: [
          [lon, lat],
          [floatingGeoPoint.lng, floatingGeoPoint.lat],
        ],
      },
      properties: {
        ApplicationStage: feature.properties.ApplicationStage,
      },
    };
  } catch (err) {
    console.warn("Invalid point for line feature:", err);
    return null;
  }
};

export const updatePinLineData = (
  map: Map,
  POIData: POIData,
  zoom: number
): { type: "FeatureCollection"; features: PinLineFeature[] } | null => {
  if (!POIData || zoom >= 15) return null;

  const offset = calculatePinLineOffset(zoom);

  const features: PinLineFeature[] = [];
  for (const feature of POIData.features) {
    const [lon, lat] = feature.geometry.coordinates;
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;

    const line = createPinLineFeature(map, feature, offset);
    if (line) features.push(line);
  }

  return {
    type: "FeatureCollection",
    features,
  };
};

export const togglePinLineVisibility = (map: Map, visible: boolean) => {
  const opacity = visible ? 0.6 : 0;
  map.setPaintProperty("line-layer", "line-opacity", opacity);
  map.setPaintProperty("line-layer-2", "line-opacity", opacity);
};
